import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ADMINISTRATIONS, DEMARCHES, HOUSING_ORGS, TOOLS } from "@/data/studentData";
import LogoTile from "@/components/LogoTile";
import BadgeCard from "@/components/BadgeCard";
import OnboardingModal from "@/components/OnboardingModal";
import TrustBadges from "@/components/TrustBadges";
import { ArrowRight, TrendingUp, Sparkles, Crown, Lock, BookText, Bell, ShieldCheck, MailCheck, Gift, MessageCircleHeart, X } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [badge, setBadge] = useState(null);
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [showIntroCta, setShowIntroCta] = useState(() => {
    try { return localStorage.getItem("intro_cta_done") !== "1"; } catch { return true; }
  });

  const sendIntroToChat = () => {
    const firstName = (user?.name || "").split(" ")[0] || "moi";
    const text = `Salut tout le monde 👋 Je m'appelle ${firstName}. C'est ma première fois ici, ravie de vous découvrir !`;
    try { sessionStorage.setItem("acc_chat_prefill", text); } catch { /* silent */ }
    try { localStorage.setItem("intro_cta_done", "1"); } catch { /* silent */ }
    setShowIntroCta(false);
  };

  const dismissIntro = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try { localStorage.setItem("intro_cta_done", "1"); } catch { /* silent */ }
    setShowIntroCta(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, b, t] = await Promise.all([
          api.get("/demarches/my"),
          api.get("/badges/me"),
          api.get("/security/2fa/status").catch(() => null),
        ]);
        setStatuses(s.data);
        setBadge(b.data);
        if (t?.data) setTwoFaStatus(t.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const { done, total, pct } = useMemo(() => {
    const doneIds = new Set(statuses.filter((s) => s.status === "done").map((s) => s.demarche_id));
    const doneCount = DEMARCHES.filter((d) => doneIds.has(d.id)).length;
    const total = DEMARCHES.length;
    const pct = total === 0 ? 0 : Math.round((doneCount / total) * 100);
    return { done: doneCount, total, pct };
  }, [statuses]);

  const quickAdmins = ADMINISTRATIONS.slice(0, 8);
  const quickHousing = HOUSING_ORGS.slice(0, 4);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <OnboardingModal />
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-white border border-slate-200 px-8 py-10 grain-bg">
        <div className="relative z-10 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold mb-3">
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            Salut {user?.name?.split(" ")[0] || "l'ami"} 👋
          </h1>
          {/* Account status badges */}
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="account-status-badges">
            {user?.email_verified && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full"
                data-testid="dash-verified-badge"
              >
                <MailCheck size={12} /> Email vérifié
              </span>
            )}
            {user?.two_fa_enabled && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-full"
                data-testid="dash-2fa-badge"
              >
                <ShieldCheck size={12} /> 2FA active
              </span>
            )}
          </div>
          {/* Trust row — discreet, links to /securite */}
          <div className="mt-3">
            <TrustBadges compact />
          </div>
          <p className="text-slate-600 mt-3 text-lg">
            Ton espace tout-en-un pour la vie étudiante en France. Logement, aides, papiers, transports — tout est ici.
          </p>
          <div className="mt-6 flex items-center gap-6">
            <div>
              <p className="text-sm text-slate-500">Progression démarches</p>
              <p className="font-display text-3xl font-bold text-slate-900" data-testid="progress-pct">{pct}%</p>
            </div>
            <div className="h-12 w-px bg-slate-200" />
            <div>
              <p className="text-sm text-slate-500">Démarches terminées</p>
              <p className="font-display text-3xl font-bold text-slate-900">{done} / {total}</p>
            </div>
          </div>
          <div className="mt-5 w-full max-w-md h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <Link
            to="/demarches"
            className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            data-testid="go-demarches-btn"
          >
            <TrendingUp size={16} /> Continuer mes démarches <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Badge */}
      {badge && <BadgeCard badge={badge} />}

      {/* 2FA + Premium bonus banner — eligible non-2FA users only */}
      {twoFaStatus && !twoFaStatus.enabled && twoFaStatus.premium_bonus_eligible && (
        <Link
          to="/settings"
          className="group relative block rounded-2xl border border-amber-300 bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50 p-5 overflow-hidden hover:shadow-md hover:border-amber-400 transition-all"
          data-testid="dash-2fa-bonus-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30">
              <Gift size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-0.5">
                Offre sécurité limitée
              </p>
              <p className="font-display font-semibold text-slate-900 text-lg leading-snug">
                Active la 2FA et reçois <span className="text-amber-700">1 mois Premium offert</span>
              </p>
              <p className="text-xs text-amber-900/70 mt-1">
                30 jours, 0 €. Une seule fois par compte. Ton accès reste actif même si tu désactives la 2FA plus tard.
              </p>
            </div>
            <span className="shrink-0 hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium group-hover:bg-slate-800 transition-colors">
              Activer <ArrowRight size={14} />
            </span>
          </div>
        </Link>
      )}

      {/* Intro to community CTA — dismissable, first visit only */}
      {showIntroCta && (
        <Link
          to="/chat"
          onClick={sendIntroToChat}
          className="group relative block rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-rose-50 p-5 overflow-hidden hover:shadow-md hover:border-violet-300 transition-all"
          data-testid="dash-intro-cta"
        >
          <button
            onClick={dismissIntro}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/80 hover:bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors z-10"
            aria-label="Masquer cette suggestion"
            data-testid="dash-intro-cta-dismiss"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-4 pr-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-violet-500/30">
              <MessageCircleHeart size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 mb-0.5">
                Communauté bienveillante
              </p>
              <p className="font-display font-semibold text-slate-900 text-lg leading-snug">
                Présente-toi à la communauté <span aria-hidden="true">👋</span>
              </p>
              <p className="text-xs text-violet-900/70 mt-1">
                Rejoins le chat anonyme. Aucun nom réel n'apparaît — tu choisis ton pseudo et ton avatar.
              </p>
            </div>
            <span className="shrink-0 hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-violet-600 text-white text-sm font-medium group-hover:bg-violet-700 transition-colors">
              C'est parti <ArrowRight size={14} />
            </span>
          </div>
        </Link>
      )}

      {/* Premium ribbon */}
      {user?.is_premium ? (
        <section className="rounded-2xl border border-yellow-200 bg-yellow-50 p-5 flex items-center gap-4" data-testid="dash-premium-active">
          <div className="w-11 h-11 rounded-xl bg-yellow-300 text-slate-900 flex items-center justify-center">
            <Crown size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-yellow-800 font-semibold">Tu es Premium</p>
            <p className="text-slate-900 font-display font-semibold">Profite des modèles, des bons plans en avance et zéro pub.</p>
          </div>
        </section>
      ) : (
        <section className="space-y-4" data-testid="dash-premium-cta">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                <Crown size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Découvrir Premium · 7 jours gratuits</p>
                <p className="text-slate-900 font-display font-semibold truncate">Modèles, bons plans en avance, sans pub — 2,99 €/mois</p>
              </div>
            </div>
            <Link
              to="/premium"
              data-testid="dash-go-premium"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800"
            >
              Essayer <ArrowRight size={14} />
            </Link>
          </div>
          {/* Locked teaser cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/modeles"
              data-testid="dash-locked-modeles"
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-5 relative overflow-hidden hover:border-yellow-300 transition-colors"
            >
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900">
                <Lock size={10} /> Premium
              </span>
              <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-500 flex items-center justify-center">
                <BookText size={18} />
              </div>
              <p className="font-display font-semibold text-slate-900 mt-3">Modèles & lettres</p>
              <p className="text-xs text-slate-500 mt-1">Bail commenté · état des lieux · résiliation · recours CAF · 8 PDFs prêts à signer</p>
              <p className="text-xs text-yellow-800 font-semibold mt-3 group-hover:underline">Déverrouiller →</p>
            </Link>
            <Link
              to="/premium"
              data-testid="dash-locked-bonsplans"
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-5 relative overflow-hidden hover:border-yellow-300 transition-colors"
            >
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900">
                <Lock size={10} /> Premium
              </span>
              <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-500 flex items-center justify-center">
                <Bell size={18} />
              </div>
              <p className="font-display font-semibold text-slate-900 mt-3">Bons plans en avant-première</p>
              <p className="text-xs text-slate-500 mt-1">Coloc, culture, transports, festivals · 24 h avant les autres</p>
              <p className="text-xs text-yellow-800 font-semibold mt-3 group-hover:underline">Déverrouiller →</p>
            </Link>
          </div>
        </section>
      )}

      {/* Quick tools */}
      <section data-testid="dash-tools">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold text-slate-900">Mes outils du quotidien</h2>
          <Link to="/bien-etre" className="text-sm text-indigo-600 hover:underline">Carte locale →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {TOOLS.map((t) => <LogoTile key={t.id} item={t} />)}
        </div>
      </section>

      {/* Key action: Mon Dossier Facile */}
      <section className="rounded-2xl border border-yellow-200 bg-yellow-50 p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-yellow-300 text-slate-900 flex items-center justify-center shrink-0">
            <Sparkles size={20} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider font-semibold text-yellow-800">Par où commencer ?</p>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-1">
              Constitue d'abord ton dossier sur <span className="underline">DossierFacile</span> (Mon Dossier Facile)
            </h2>
            <p className="text-slate-700 mt-2">
              C'est un service public gratuit qui certifie tous tes justificatifs. Les bailleurs acceptent ton dossier
              plus vite, tu évites les arnaques et tu ne refais pas ton dossier à chaque visite.
              <strong> Un bon dossier = un logement plus vite.</strong>
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href="https://www.dossierfacile.logement.gouv.fr"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800"
                data-testid="dossierfacile-cta"
              >
                Créer mon DossierFacile <ArrowRight size={14} />
              </a>
              <Link
                to="/logement"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-slate-900 text-sm border border-slate-200 hover:bg-slate-50"
                data-testid="go-logement-btn"
              >
                Guide logement complet
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Admin shortcuts */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">Administrations essentielles</h2>
          <Link to="/administrations" className="text-sm text-indigo-600 hover:underline" data-testid="see-all-admins">
            Voir tout →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {quickAdmins.map((a) => <LogoTile key={a.id} item={a} />)}
        </div>
      </section>

      {/* Housing orgs */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">Trouver un logement</h2>
          <Link to="/logement" className="text-sm text-indigo-600 hover:underline" data-testid="see-all-housing">
            Tout le guide →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {quickHousing.map((h) => <LogoTile key={h.id} item={h} />)}
        </div>
      </section>
    </div>
  );
}
